// Cell encoding (RFC 4180 quoting + the SEC-2 formula-injection guard) is the
// shared @cca/safe csvCell — one escaper per sink (security.md SEC-3). The
// local escaper this replaced exempted anything *starting* `+digit`, which let
// `+2+5+cmd|…` payloads through as "numeric"; csvCell's signed-number
// carve-out is a full match, so genuine budget figures still parse.
import { csvCell } from '@cca/safe';
import { yearLabel } from './format';
import type { DerivedBudget } from './totals';

// Build a CSV string from a derived budget. Mirrors v5's section
// structure: section headers, category groups, subtotals, totals,
// Net Operating, project blocks, total non-op, grand total. Year
// columns include the global CPI rate annotation.
//
// Pure function — keeps testability. The download wrapper just creates
// a Blob and triggers a click.

const round = (n: number): number => Math.round(n);

type Cell = string | number;
type Row = Cell[];

const blankRow: Row = ['', '', '', '', '', '', '', '', ''];

export function buildBudgetCsv(derived: DerivedBudget, baseYear: number): string {
  const { defaults } = derived;
  const headers: Row = [
    'Line #',
    'Section',
    'Account',
    'Code',
    `${yearLabel(baseYear, 0)} (Current)`,
    `${yearLabel(baseYear, 1)} (CPI ${defaults.cpiY1}%)`,
    `${yearLabel(baseYear, 2)} (CPI ${defaults.cpiY2}%)`,
    `${yearLabel(baseYear, 3)} (CPI ${defaults.cpiY3}%)`,
    'Notes',
  ];

  const rows: Row[] = [headers];
  let lineNo = 1;

  const emitGroup = (group: (typeof derived.incomeByCategory)[number]) => {
    const primaryItems = group.category.type === 'income' ? group.incomeItems : group.expenseItems;
    const deductionItems =
      group.category.type === 'income' ? group.expenseItems : group.incomeItems;
    if (primaryItems.length === 0 && deductionItems.length === 0) return;
    rows.push(['', group.category.name, '', '', '', '', '', '', '']);
    for (const item of primaryItems) {
      rows.push([
        lineNo++,
        '',
        item.name,
        item.code,
        round(item.current),
        round(item.y1),
        round(item.y2),
        round(item.y3),
        item.note,
      ]);
    }
    if (deductionItems.length > 0) {
      const label = group.category.type === 'income' ? '  Deductions' : '  Offsets';
      rows.push(['', label, '', '', '', '', '', '', '']);
      for (const item of deductionItems) {
        rows.push([
          lineNo++,
          '',
          `  ${item.name}`,
          item.code,
          round(item.current),
          round(item.y1),
          round(item.y2),
          round(item.y3),
          item.note,
        ]);
      }
    }
    rows.push([
      '',
      `Net — ${group.category.name}`,
      '',
      '',
      round(group.net.current),
      round(group.net.y1),
      round(group.net.y2),
      round(group.net.y3),
      '',
    ]);
  };

  // ── Operating Income ──────────────────────────────────────────────
  rows.push(['', 'OPERATING — INCOME', '', '', '', '', '', '', '']);
  for (const group of derived.incomeByCategory) emitGroup(group);
  rows.push([
    '',
    'TOTAL OPERATING INCOME',
    '',
    '',
    round(derived.operatingIncomeTotals.current),
    round(derived.operatingIncomeTotals.y1),
    round(derived.operatingIncomeTotals.y2),
    round(derived.operatingIncomeTotals.y3),
    '',
  ]);

  rows.push(blankRow);

  // ── Operating Expenses ────────────────────────────────────────────
  rows.push(['', 'OPERATING — EXPENSES', '', '', '', '', '', '', '']);
  for (const group of derived.expensesByCategory) emitGroup(group);
  rows.push([
    '',
    'TOTAL OPERATING EXPENSES',
    '',
    '',
    round(derived.operatingExpenseTotals.current),
    round(derived.operatingExpenseTotals.y1),
    round(derived.operatingExpenseTotals.y2),
    round(derived.operatingExpenseTotals.y3),
    '',
  ]);
  rows.push([
    '',
    '── NET OPERATING ──',
    '',
    '',
    round(derived.operatingNet.current),
    round(derived.operatingNet.y1),
    round(derived.operatingNet.y2),
    round(derived.operatingNet.y3),
    '',
  ]);
  rows.push(blankRow);

  // ── Projects ──────────────────────────────────────────────────────
  if (derived.projects.length > 0) {
    rows.push(['', 'DISCRETE ACTIVITIES', '', '', '', '', '', '', '']);
    for (const p of derived.projects) {
      rows.push(['', `Project: ${p.project.name}`, '', '', '', '', '', '', '']);
      if (p.income.length > 0) {
        rows.push(['', '  Income', '', '', '', '', '', '', '']);
        for (const item of p.income) {
          rows.push([
            lineNo++,
            '',
            `  ${item.name}`,
            item.code,
            round(item.current),
            round(item.y1),
            round(item.y2),
            round(item.y3),
            item.note,
          ]);
        }
      }
      if (p.expenses.length > 0) {
        rows.push(['', '  Expenses', '', '', '', '', '', '', '']);
        for (const item of p.expenses) {
          rows.push([
            lineNo++,
            '',
            `  ${item.name}`,
            item.code,
            round(item.current),
            round(item.y1),
            round(item.y2),
            round(item.y3),
            item.note,
          ]);
        }
      }
      rows.push([
        '',
        `  Net — ${p.project.name}`,
        '',
        '',
        round(p.net.current),
        round(p.net.y1),
        round(p.net.y2),
        round(p.net.y3),
        '',
      ]);
    }
    rows.push([
      '',
      'TOTAL DISCRETE NET',
      '',
      '',
      round(derived.totalProjectsNet.current),
      round(derived.totalProjectsNet.y1),
      round(derived.totalProjectsNet.y2),
      round(derived.totalProjectsNet.y3),
      '',
    ]);
    rows.push(blankRow);
  }

  rows.push([
    '',
    '═══ GRAND TOTAL NET ═══',
    '',
    '',
    round(derived.grandNet.current),
    round(derived.grandNet.y1),
    round(derived.grandNet.y2),
    round(derived.grandNet.y3),
    '',
  ]);

  // `?? ''` keeps the retired escaper's null/undefined tolerance: Cell is typed
  // string | number, but note fields on imported data have arrived undefined.
  return rows.map((r) => r.map((c) => csvCell(c ?? '')).join(',')).join('\n');
}

export function csvFilename(baseYear: number): string {
  const date = new Date().toISOString().slice(0, 10);
  return `CCAANZ_Budget_${yearLabel(baseYear, 1)}-${yearLabel(baseYear, 3)}_${date}.csv`;
}

export function downloadBudgetCsv(derived: DerivedBudget, baseYear: number): void {
  const csv = buildBudgetCsv(derived, baseYear);
  // Prepend a UTF-8 BOM so Excel / Numbers open it as UTF-8 and don't
  // mangle the em-dashes (— would otherwise show as â— under Win-1252).
  const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = csvFilename(baseYear);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
